import React, { useState } from 'react';

import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';

import Button from 'components/Button';
import Message from 'components/Message';
import PageTurner from 'components/PageTurner/PageTurner';
import { SvgArrow } from 'components/Svgs';
import { Table } from 'components/Table';
import { ICONS } from 'constants/icons';
import { bem } from 'helpers/class-name';

import { SORTS, sharedAcrossGroup, sortItems } from './SkuItemReports.helpers';
import messages from './SkuItemReports.messages';
import VenueLabel from './SkuReportsVenueLabel';

const { element } = bem('sku-item-reports');

const EMPTY_VALUE = '—';
const PAGE_SIZE = 20;

// Column widths in px from the design's 1015px content column, rendered as
// percentages of the visible columns' total so dropping a column renormalizes.
const COLUMNS = [
  { key: 'item', label: messages.itemColumn, width: 183 },
  { key: 'location', label: messages.locationColumn, width: 226 },
  { key: 'menu', label: messages.menuColumn, width: 144 },
  { key: 'category', label: messages.categoryColumn, width: 123 },
  { key: 'unitsSold', label: messages.unitsSoldColumn, width: 115 },
  { key: 'orders', label: messages.ordersColumn, width: 78 },
  { key: 'earnings', label: messages.earningsColumn, align: 'right', width: 145 },
];

const MOBILE_COLUMNS = [
  { key: 'item', label: messages.itemColumn },
  { key: 'earnings', label: messages.earningsColumn, align: 'right' },
];

const ARIA_SORT = { asc: 'ascending', desc: 'descending' };

const itemKey = item => `${item.name}::${item.earnings?.currencyCode || ''}`;

const isGrouped = item => item.venueItems.length > 1;

const distinctLabels = (venueItems, pick) => [...new Set(venueItems.flatMap(pick))];

const moneyShape = PropTypes.shape({ amount: PropTypes.number, currencyCode: PropTypes.string });

const venueItemShape = PropTypes.shape({
  itemId: PropTypes.string.isRequired,
  venueId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  venueName: PropTypes.string,
  venueSubtitle: PropTypes.string,
  name: PropTypes.string.isRequired,
  menus: PropTypes.arrayOf(PropTypes.string).isRequired,
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  unitsSold: PropTypes.number.isRequired,
  ordersCount: PropTypes.number.isRequired,
  earnings: moneyShape,
});

const itemShape = PropTypes.shape({
  name: PropTypes.string.isRequired,
  unitsSold: PropTypes.number.isRequired,
  ordersCount: PropTypes.number.isRequired,
  earnings: moneyShape,
  venueItems: PropTypes.arrayOf(venueItemShape).isRequired,
});

const MenusLabel = ({ venueItems }) =>
  distinctLabels(venueItems, venueItem => venueItem.menus).join(', ') || EMPTY_VALUE;

MenusLabel.propTypes = { venueItems: PropTypes.arrayOf(venueItemShape).isRequired };

const CategoriesLabel = ({ venueItems }) => {
  const intl = useIntl();
  const labels = distinctLabels(venueItems, venueItem => venueItem.categories);
  if (venueItems.some(venueItem => !venueItem.categories.length)) {
    labels.push(intl.formatMessage(messages.uncategorized));
  }
  return labels.join(', ') || EMPTY_VALUE;
};

CategoriesLabel.propTypes = { venueItems: PropTypes.arrayOf(venueItemShape).isRequired };

const LocationLabel = ({ item }) => {
  const count = new Set(item.venueItems.map(venueItem => venueItem.venueId)).size;
  if (count > 1) return <Message message={messages.locationsCount} values={{ count }} />;
  const [venueItem] = item.venueItems;
  return <VenueLabel name={venueItem?.venueName} subtitle={venueItem?.venueSubtitle} />;
};

LocationLabel.propTypes = { item: itemShape.isRequired };

const EarningsCell = ({ earnings }) => (
  <td className={element('earnings')}>
    {earnings ? <Message format="currency" value={earnings.amount} currency={earnings.currencyCode} /> : EMPTY_VALUE}
  </td>
);

EarningsCell.propTypes = { earnings: moneyShape };

const ExpandToggle = ({ name, isExpanded, onToggle }) => {
  const intl = useIntl();
  return (
    <button
      type="button"
      className={element('expand-toggle')}
      aria-expanded={isExpanded}
      aria-label={intl.formatMessage(messages.toggleBreakdown, { name })}
      onClick={onToggle}>
      <SvgArrow direction={isExpanded ? 'up' : 'down'} aria-hidden="true" />
    </button>
  );
};

ExpandToggle.propTypes = {
  name: PropTypes.string.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const ItemNameCell = ({ item, isExpanded, onToggle, sublabel }) => (
  <td>
    <span className={element('item-cell')}>
      {isGrouped(item) && <ExpandToggle name={item.name} isExpanded={isExpanded} onToggle={onToggle} />}
      <span>
        <span className={element('item-name')}>{item.name}</span>
        {sublabel && <span className={element('item-sublabel')}>{sublabel}</span>}
      </span>
    </span>
  </td>
);

ItemNameCell.propTypes = {
  item: itemShape.isRequired,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  sublabel: PropTypes.node,
};

const ItemRow = ({ item, isExpanded, onToggle, showLocationColumn }) => {
  const intl = useIntl();
  return (
    <tr>
      <ItemNameCell item={item} isExpanded={isExpanded} onToggle={onToggle} />
      {showLocationColumn && (
        <td>
          <LocationLabel item={item} />
        </td>
      )}
      <td>
        <MenusLabel venueItems={item.venueItems} />
      </td>
      <td>
        <CategoriesLabel venueItems={item.venueItems} />
      </td>
      <td>{intl.formatNumber(item.unitsSold)}</td>
      <td>{intl.formatNumber(item.ordersCount)}</td>
      <EarningsCell earnings={item.earnings} />
    </tr>
  );
};

ItemRow.propTypes = {
  item: itemShape.isRequired,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  showLocationColumn: PropTypes.bool,
};

const SubRow = ({ venueItem, shared, showLocationColumn }) => {
  const intl = useIntl();
  return (
    <tr className={element('sub-row')}>
      <td>
        <span className={element('sub-item-name')}>{venueItem.name}</span>
      </td>
      {showLocationColumn && (
        <td>
          {shared.venue ? EMPTY_VALUE : <VenueLabel name={venueItem.venueName} subtitle={venueItem.venueSubtitle} />}
        </td>
      )}
      <td>{shared.menus ? EMPTY_VALUE : <MenusLabel venueItems={[venueItem]} />}</td>
      <td>{shared.categories ? EMPTY_VALUE : <CategoriesLabel venueItems={[venueItem]} />}</td>
      <td>{intl.formatNumber(venueItem.unitsSold)}</td>
      <td>{intl.formatNumber(venueItem.ordersCount)}</td>
      <EarningsCell earnings={venueItem.earnings} />
    </tr>
  );
};

const sharedShape = PropTypes.shape({
  venue: PropTypes.bool.isRequired,
  menus: PropTypes.bool.isRequired,
  categories: PropTypes.bool.isRequired,
});

SubRow.propTypes = {
  venueItem: venueItemShape.isRequired,
  shared: sharedShape.isRequired,
  showLocationColumn: PropTypes.bool,
};

const MobileItemRow = ({ item, isExpanded, onToggle, showLocationColumn }) => (
  <tr>
    <ItemNameCell
      item={item}
      isExpanded={isExpanded}
      onToggle={onToggle}
      sublabel={showLocationColumn && <LocationLabel item={item} />}
    />
    <EarningsCell earnings={item.earnings} />
  </tr>
);

MobileItemRow.propTypes = {
  item: itemShape.isRequired,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  showLocationColumn: PropTypes.bool,
};

const MobileSubRow = ({ venueItem, shared }) => (
  <tr className={element('sub-row')}>
    <td>
      <span className={element('sub-item-name')}>
        <span>{venueItem.name}</span>
        {!shared.venue && (
          <span className={element('item-sublabel')}>
            <VenueLabel name={venueItem.venueName} subtitle={venueItem.venueSubtitle} />
          </span>
        )}
      </span>
    </td>
    <EarningsCell earnings={venueItem.earnings} />
  </tr>
);

MobileSubRow.propTypes = { venueItem: venueItemShape.isRequired, shared: sharedShape.isRequired };

const EmptyState = () => (
  <tr>
    <td colSpan={100}>
      <div className={element('empty')}>
        <Isvg className={element('empty-icon')} src={ICONS.inbox} />
        <div className={element('empty-title')}>
          <Message message={messages.emptyTitle} />
        </div>
        <div className={element('empty-body')}>
          <Message message={messages.emptyBody} />
        </div>
      </div>
    </td>
  </tr>
);

const ErrorState = ({ onRetry }) => (
  <tr>
    <td colSpan={100}>
      <div className={element('empty')}>
        <div className={element('empty-title')}>
          <Message message={messages.errorTitle} />
        </div>
        <div className={element('empty-body')}>
          <Message message={messages.errorBody} />
        </div>
        <Button styled="secondary" onClick={onRetry}>
          <Message message={messages.retry} />
        </Button>
      </div>
    </td>
  </tr>
);

ErrorState.propTypes = { onRetry: PropTypes.func.isRequired };

const SkuItemReportsTable = ({
  items,
  filtersKey,
  showLocationColumn = true,
  isMobile,
  isLoading,
  isError,
  onRetry,
}) => {
  const [sort, setSort] = useState({ key: 'unitsSold', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [lastFiltersKey, setLastFiltersKey] = useState(filtersKey);

  if (filtersKey !== lastFiltersKey) {
    setLastFiltersKey(filtersKey);
    setPage(1);
  }

  const sorted = sortItems(items, sort);
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageItems = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleSort = key => {
    setPage(1);
    setSort(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: SORTS[key].defaultDirection }
    );
  };

  const toggleExpanded = key =>
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  const columns = (isMobile ? MOBILE_COLUMNS : COLUMNS).filter(
    column => showLocationColumn || column.key !== 'location'
  );
  const totalWidth = columns.reduce((sum, column) => sum + (column.width || 0), 0);
  const thStyle = column => (column.width ? { width: `${(column.width / totalWidth) * 100}%` } : undefined);

  const header = (
    <tr>
      {columns.map(column => {
        const isActive = sort.key === column.key;
        const thClassName = column.align === 'right' ? element('th-right') : undefined;
        if (!SORTS[column.key]) {
          return (
            <th className={thClassName} key={column.key} style={thStyle(column)}>
              <Message message={column.label} />
            </th>
          );
        }
        return (
          <th
            aria-sort={isActive ? ARIA_SORT[sort.direction] : undefined}
            className={thClassName}
            key={column.key}
            style={thStyle(column)}>
            <button className={element('sort-toggle')} onClick={() => handleSort(column.key)} type="button">
              <Message message={column.label} />
              <SvgArrow direction={isActive && sort.direction === 'asc' ? 'up' : 'down'} aria-hidden="true" />
            </button>
          </th>
        );
      })}
    </tr>
  );

  const renderBody = () => {
    if (isError) return [<ErrorState key="error" onRetry={onRetry} />];
    if (!sorted.length) return [<EmptyState key="empty" />];
    const Row = isMobile ? MobileItemRow : ItemRow;
    const Sub = isMobile ? MobileSubRow : SubRow;
    return pageItems.flatMap(item => {
      const key = itemKey(item);
      const isExpanded = isGrouped(item) && expandedKeys.has(key);
      const rows = [
        <Row
          isExpanded={isExpanded}
          item={item}
          key={key}
          onToggle={() => toggleExpanded(key)}
          showLocationColumn={showLocationColumn}
        />,
      ];
      if (isExpanded) {
        const shared = sharedAcrossGroup(item.venueItems);
        rows.push(
          ...item.venueItems.map(venueItem => (
            <Sub
              key={`${key}::${venueItem.itemId}::${venueItem.venueId}`}
              shared={shared}
              showLocationColumn={showLocationColumn}
              venueItem={venueItem}
            />
          ))
        );
      }
      return rows;
    });
  };

  return (
    <>
      <Table className={element('table')} header={header} isLoading={isLoading}>
        {!isLoading && renderBody()}
      </Table>
      {!isError && (
        <PageTurner
          className={element('page-turner')}
          count={sorted.length}
          endNumber={Math.min(safePage * PAGE_SIZE, sorted.length)}
          isLoading={isLoading}
          isOnFirstPage={safePage === 1}
          isOnLastPage={safePage === pageCount}
          startNumber={sorted.length ? (safePage - 1) * PAGE_SIZE + 1 : 0}
          viewNextPage={() => setPage(safePage + 1)}
          viewPrevPage={() => setPage(safePage - 1)}
        />
      )}
    </>
  );
};

SkuItemReportsTable.propTypes = {
  items: PropTypes.arrayOf(itemShape).isRequired,
  filtersKey: PropTypes.string,
  showLocationColumn: PropTypes.bool,
  isMobile: PropTypes.bool,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  onRetry: PropTypes.func.isRequired,
};

export default SkuItemReportsTable;
